import { useRef, useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Form, Modal, Radio } from 'antd'
import {
  enterpriseSupplementDocumentsSchema,
  type DocumentAmendment,
  type EnterpriseSummary as Enterprise
} from '@super-mega/server/contracts/order'
import { useTRPC } from '@/trpc/react.ts'
import { EnterpriseDocumentsEditor } from './enterprise-draft-editor.tsx'
import { EMPTY_DOCUMENTS } from './enterprise-labels.ts'
import { enterpriseModalProps } from './enterprise-modal-layout.ts'
import { EnterpriseSummary } from './enterprise-summary.tsx'

export type EnterpriseSupplementModalProps = {
  subjectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  currentOrder?: { type: 'rental' | 'service'; id: number }
  syncCurrentRentalOrder?: boolean
  onSuccess?: () => void
  inline?: boolean
  onBusyChange?: (busy: boolean) => void
}

export function EnterpriseSupplementModal(props: EnterpriseSupplementModalProps) {
  const trpc = useTRPC()
  const detail = useQuery({
    ...trpc.order.subjects.detail.queryOptions({ subjectId: props.subjectId }),
    enabled: props.open
  })
  if (!detail.data) {
    return (
      <Modal
        open={props.open}
        title="补充服务对象资料"
        footer={null}
        onCancel={() => props.onOpenChange(false)}
        loading={detail.isLoading}
      >
        {detail.error && (
          <Alert
            type="error"
            title={detail.error.message}
            action={<Button onClick={() => void detail.refetch()}>重试</Button>}
          />
        )}
      </Modal>
    )
  }
  return <SupplementForm key={props.subjectId} {...props} initial={detail.data} />
}

export function EnterpriseSupplementEditor({
  initial,
  onBusyChange
}: {
  initial: Enterprise
  onBusyChange: (busy: boolean) => void
}) {
  return (
    <SupplementForm
      initial={initial}
      subjectId={initial.id}
      open
      inline
      onOpenChange={() => {}}
      onBusyChange={onBusyChange}
    />
  )
}

function SupplementForm({
  initial,
  subjectId,
  open,
  onOpenChange,
  currentOrder,
  syncCurrentRentalOrder = false,
  onSuccess,
  inline = false,
  onBusyChange
}: EnterpriseSupplementModalProps & { initial: Enterprise }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [baseline, setBaseline] = useState(initial)
  const [documents, setDocuments] = useState<DocumentAmendment>(EMPTY_DOCUMENTS)
  const [ready, setReady] = useState(true)
  const [documentsGeneration, setDocumentsGeneration] = useState(0)
  const [sync, setSync] = useState(false)
  const [error, setError] = useState<string>()
  const [reviewing, setReviewing] = useState(false)
  const submission = useRef<{ payload: string; key: string } | undefined>(undefined)
  const mutation = useMutation(trpc.order.subjects.supplementDocuments.mutationOptions())
  const save = async () => {
    if (!ready || mutation.isPending || reviewing) {
      return false
    }
    const payload = {
      subjectId,
      expectedVersion: baseline.version,
      documents,
      syncRentalOrderId:
        (syncCurrentRentalOrder || sync) && currentOrder?.type === 'rental' ? currentOrder.id : undefined
    }
    const serialized = JSON.stringify(payload)
    if (submission.current?.payload !== serialized) {
      submission.current = { payload: serialized, key: crypto.randomUUID() }
    }
    const parsed = enterpriseSupplementDocumentsSchema.safeParse({
      ...payload,
      submissionKey: submission.current.key
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message)
      return false
    }
    onBusyChange?.(true)
    try {
      const result = await mutation.mutateAsync(parsed.data)
      await Promise.all([
        cache.invalidateQueries(trpc.order.subjects.list.queryFilter()),
        cache.invalidateQueries(trpc.order.subjects.options.queryFilter()),
        cache.invalidateQueries(trpc.order.subjects.detail.queryFilter()),
        cache.invalidateQueries(trpc.company.reusable.queryFilter()),
        cache.invalidateQueries(trpc.company.search.queryFilter()),
        cache.invalidateQueries(trpc.identity.reusable.queryFilter()),
        cache.invalidateQueries(trpc.identity.search.queryFilter()),
        cache.invalidateQueries(trpc.license.list.queryFilter()),
        ...(result.syncedRentalOrderId
          ? [
              cache.invalidateQueries(trpc.order.rental.list.queryFilter()),
              cache.invalidateQueries(
                trpc.order.rental.detail.queryFilter({ rentalOrderId: result.syncedRentalOrderId })
              )
            ]
          : [])
      ])
      setBaseline(result.subject)
      setDocuments(EMPTY_DOCUMENTS)
      setReady(true)
      setDocumentsGeneration((previous) => previous + 1)
      setSync(false)
      setError(undefined)
      submission.current = undefined
      onSuccess?.()
      onOpenChange(false)
      void message.success(
        result.syncedRentalOrderId ? '企业资料已补录，并同步当前租赁订单' : '企业资料已补录，历史订单资料保持原样'
      )
      onBusyChange?.(false)
      return true
    } catch (cause) {
      onBusyChange?.(false)
      setError(cause instanceof Error ? cause.message : '补录失败，候选资料已保留')
      return false
    }
  }
  const content = (
    <div className="flex flex-col gap-6">
      {error && (
        <Alert
          className={'mb-2!'}
          type="error"
          showIcon
          title={error}
          description={
            error.includes('识别') || error.includes('复核')
              ? '候选图片已保留。请在下方对证件重新上传识别并核对；读取企业档案不能刷新图片识别结果。'
              : '本地候选已保留。如企业被他人修改，请读取最新资料并重新核对后提交。'
          }
          action={
            !(error.includes('识别') || error.includes('复核')) && (
              <Button
                disabled={mutation.isPending}
                loading={reviewing}
                onClick={async () => {
                  setReviewing(true)
                  try {
                    setBaseline(
                      await cache.fetchQuery({
                        ...trpc.order.subjects.detail.queryOptions({ subjectId }),
                        staleTime: 0
                      })
                    )
                    setError(undefined)
                  } catch (cause) {
                    setError(cause instanceof Error ? cause.message : '读取失败')
                  }
                  setReviewing(false)
                }}
              >
                读取最新资料
              </Button>
            )
          }
        />
      )}
      <section>
        <h3 className="mb-3 text-sm font-semibold">当前服务对象 · #{subjectId}</h3>
        <EnterpriseSummary enterprise={baseline} />
      </section>
      <EnterpriseDocumentsEditor
        key={documentsGeneration}
        value={documents}
        onChange={setDocuments}
        onReadyChange={setReady}
        disabled={mutation.isPending || reviewing}
        current={baseline}
      />
      <section className="border-border border-t pt-4">
        <h3 className="mb-2 text-sm font-semibold">保存范围</h3>
        {currentOrder?.type === 'rental' && syncCurrentRentalOrder ? (
          <p className="text-muted-foreground m-0 text-sm">
            更新服务对象档案，并补齐当前租赁订单 #{currentOrder.id}{' '}
            的空缺资料。保存后自动重新检查合同生成条件，资料齐全即可继续保存并打印合同。
          </p>
        ) : currentOrder?.type === 'rental' ? (
          <>
            <Radio.Group
              aria-label="保存范围"
              value={sync}
              disabled={mutation.isPending || reviewing}
              onChange={(event) => setSync(event.target.value)}
              className="flex! flex-col gap-3"
            >
              <Radio value={false}>仅更新服务对象档案（默认）</Radio>
              <Radio value={true}>更新企业档案，并补齐租赁订单 #{currentOrder.id} 的空资料</Radio>
            </Radio.Group>
            <p className="text-muted-foreground mt-3 mb-0 text-sm">
              {sync
                ? '不受订单状态限制。只填空缺，不覆盖订单和场地占用中已有的不同企业或身份；不满足条件时，本次企业档案也不会保存。更换本单已有企业或资料，请在订单编辑页点击“修改资料”。'
                : '本次只保存企业档案，当前订单仍保留原资料。若要让待处理订单使用修改后的资料，请在订单编辑页点击“修改资料”，核对后保存订单。'}
            </p>
          </>
        ) : (
          <p className="text-muted-foreground m-0 text-sm">仅更新服务对象档案，已有订单保留原资料。</p>
        )}
        <p className="text-muted-foreground mt-2 mb-0 text-xs">
          未替换的档案资料继续保留；历史合同、账单及其他订单不受本次补录影响。
        </p>
      </section>
    </div>
  )
  if (inline) {
    return (
      <Form layout="vertical" onFinish={save} className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto pb-6">{content}</div>
        <div className="border-border flex shrink-0 items-center justify-between gap-4 border-t pt-4">
          <span className="text-content-muted text-xs">逐个保存，已有订单保留原资料</span>
          <Button type="primary" htmlType="submit" loading={mutation.isPending} disabled={!ready || reviewing}>
            保存资料
          </Button>
        </div>
      </Form>
    )
  }
  return (
    <ModalForm
      open={open}
      onOpenChange={onOpenChange}
      title="补充服务对象资料"
      width="64rem"
      layout="vertical"
      modalProps={{
        ...enterpriseModalProps,
        mask: { closable: false },
        keyboard: !mutation.isPending,
        closable: !mutation.isPending
      }}
      submitter={{
        searchConfig: { submitText: '保存资料', resetText: '取消' },
        submitButtonProps: { loading: mutation.isPending, disabled: !ready || reviewing },
        resetButtonProps: { disabled: mutation.isPending }
      }}
      onFinish={save}
    >
      {content}
    </ModalForm>
  )
}
